<template>
    <div>
        <div
        v-if="isOfflineMode && startedWidget === 'program' && !isLoading"
            id="networkIconsStatus"
            :class="{ 'openedSidebar': isOpenedSidebar }"
        >
            <div class="left-edge-rounded pa-2">
                <v-tooltip 
                    content-class="bottom" 
                    bottom
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon
                            v-bind="attrs"
                            v-on="on"
                        >
                            icon-wifi
                        </v-icon>
                    </template>
                    <span>
                        {{ isOnline ? $t("flashMessage.networkStatus.online") : $t("flashMessage.networkStatus.offline")}}
                    </span>
                </v-tooltip>
                <span
                    class="light-notification"
                    :class="{
                        red: !isOnline,
                        green: isOnline
                    }"
                />
            </div>
            <div
                class="right-edge-rounded pa-2"
                :class="{ disabled: !isOnline }"
            >
                <a @click.prevent="isOnline ? startCache() : null">
                    <v-tooltip 
                        content-class="bottom" 
                        bottom
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon
                                v-bind="attrs"
                                v-on="on"
                            >
                                icon-dl
                            </v-icon>
                        </template>
                        <span>
                            {{ isOnline ? $t("pwa.offline.downloadResources") : $t("pwa.offline.disabledFeature") }}
                        </span>
                    </v-tooltip>
                </a>
                <span
                    v-if="lastCacheDate"
                    class="light-notification green"
                />
            </div>

            <div
                class="lastCacheInfo"
                v-if="lastCacheDate"
            >
                <v-tooltip 
                    content-class="bottom" 
                    bottom
                >
                    <template v-slot:activator="{ on, attrs }">
                        <a
                            class="rounded toggleBtn pa-1"
                            @click.prevent="toggleCacheDetails()"
                            v-bind="attrs"
                            v-on="on"
                        >
                            i
                        </a>
                    </template>
                    <span>
                        {{ $t("pwa.offline.lastDownloadDate") }} : {{ lastCacheDate }}
                    </span>
                </v-tooltip>

                <v-tooltip 
                    content-class="bottom" 
                    bottom
                >
                    <template v-slot:activator="{ on, attrs }">
                        <a
                            id="trashBtn"
                            v-bind="attrs"
                            v-on="on"
                            class="rounded"
                            :class="{ disabled: !lastCacheDate }"
                            @click="toggleConfirmDeleteAction();"
                        >
                            <v-icon>
                                icon-trash
                            </v-icon>
                        </a>
                        <a
                            id="confirmDeleteBtn"
                            ref="deleteBtn"
                            :class="{
                                opened: confirmIsOpened,
                                closed: !confirmIsOpened
                            }"
                            @click.prevent="deleteCache()"
                        >
                            <span class="text">
                                {{ !deleteActionConfirmed ? $t("pwa.offline.confirmButtonText") : $t("pwa.offline.deleteButtonText") }}
                            </span>
                        </a>
                    </template>
                    <span>
                        {{ $t("pwa.offline.removeResources") }}
                    </span>
                </v-tooltip>
            </div>
        </div>
        <Loader
            v-if="isLoadingOffline"
            :messageType="'offline'"
        />
    </div>
</template>

<script>
    import { EventBus } from '@/event-bus';
    import { mapActions, mapGetters } from 'vuex';

    import { offlineDependencies } from '@/private/offline-dependencies';
    import Loader from "@/components/Loader";

    export default {
        name: 'NetworkStatus',
        components: {
            Loader
        },
        props: [
            'data',
            'isOpenedSidebar'
        ],
        data: () => ({
            lastCacheDate: null,
            confirmIsOpened : false,
            deleteActionConfirmed: false,
            cacheKey: null,
            widgetList: [],
            apiRequestsList: [],
            requestList: {},
            urlRequestList: {},
            programUrl: null,
            startCaching: false,
            timeoutReset: null,
            consoleStyle: {
                dependencies: 'background: #b6490b; color: white; border-radius : 10px',
                image: 'background: #00897b; color: white; border-radius : 10px',
                request: 'background: #01579b; color: white; border-radius: 10px',
                resource: 'background: #3f1dcb; color: white; border-radius: 10px',
                origin: 'background: #c43e00; color: white; border-radius: 10px',
                counter: 'background-color:grey; color:white; border-radius: 10px',
                text: 'color: #135aaa; font-weight: bold;'
            },
            disabledWidgets: ['models', 'district-video', 'map-view', 'documentation'],
            offlineParameters: '&offline',
            isLoadingOffline: false
        }),
        computed: {
            ...mapGetters([
                'isOfflineMode',
                'isOnline',
                'isLoading',
                'isReviewUse',
                'language',
                'protocol',
                'startedWidget'
            ]),
            apiUrl() {
                return 'https://' + (this.isReviewUse ? process.env.API_LIST.REVIEW : process.env.API_LIST.FO);
            }
        },
        mounted() {
            let url = new URL(window.location.href);
            this.cacheKey = url.searchParams.get('id') + '-' + url.searchParams.get('key');
            this.lastCacheDate = this.getCookie('cacheDate-' + this.cacheKey);
            this.programUrl = this.apiUrl + '/request-data/program' + window.location.search;
            this.initServiceWorkers();

            EventBus.$on('setWidgetList', (widgetList) => {
                this.widgetList = widgetList.filter(widget => !this.disabledWidgets.includes(widget.id));
            });
            window.addEventListener('online', () => {
                this.updateOnlineStatus();
            });

            window.addEventListener('offline', () => {
                this.updateOnlineStatus();
            });
        },
        methods: {
            ...mapActions([
                'setNetworkStatus'
            ]),
            initServiceWorkers() {
                if ('serviceWorker' in navigator && this.isOfflineMode && window.location.pathname == '/programme') {
                    window.addEventListener('load', () => {
                        navigator.serviceWorker.register('/service-worker.js?cacheKey=' + this.cacheKey + '&status=' + (this.isOnline ? 'online' : 'offline')).then(registration => {
                            if (navigator.serviceWorker.controller) {
                                // let the application know our service worker is ready
                                window['serviceWorkerReady'] = true;
                                window.dispatchEvent(new CustomEvent('service-worker-ready'));
                            }

                            // A wild service worker has appeared in reg.installing and maybe in waiting!
                            const newWorker = registration.installing;
                            const waitingWoker = registration.waiting;

                            if (newWorker) {
                                if (newWorker.state === 'activated' && !waitingWoker) {
                                    // - no more window.location.reload();
                                    newWorker.postMessage({ type: 'CLIENTS_CLAIM' });
                                }
                                newWorker.addEventListener('statechange', () => {
                                    // newWorker.state has changed
                                    if (newWorker.state === 'activated' && !waitingWoker) {
                                        // - no more window.location.reload();
                                        newWorker.postMessage({ type: 'CLIENTS_CLAIM' });
                                    }
                                });
                            }
                        })
                        .catch(err => {
                            console.log('service worker could not be registered', err);
                        });
                    });
                } else if ('serviceWorker' in navigator) {
                    // Remove all service workers
                    navigator.serviceWorker.getRegistrations().then(registrations => {
                        this.deleteCache();
                        if (registrations.length)
                            for (let registration of registrations)
                                registration.unregister();
                    });
                }
            },
            updateOnlineStatus() {
                this.setNetworkStatus(navigator.onLine);
                let networkStatusText = (this.isOnline != null && !this.isOnline) ? this.$t("flashMessage.networkStatus.offline") : this.$t("flashMessage.networkStatus.online");
                EventBus.$emit('displayFlashMessage', networkStatusText);
            },
            getCookie(name) {
                // Split cookie string and get all individual name=value pairs in an array
                var cookieArr = document.cookie.split(';');

                // Loop through the array elements
                for (var i = 0; i < cookieArr.length; i++) {
                    var cookiePair = cookieArr[i].split('=');

                    // Removing whitespace at the beginning of the cookie name and compare it with the given string
                    if (name == cookiePair[0].trim()) {
                        // Decode the cookie value and return
                        return decodeURIComponent(cookiePair[1]);
                    }
                }
                return null;
            },
            setCookie(name, value, daysToLive) {
                // Encode value in order to escape semicolons, commas, and whitespace
                var cookie = name + '=' + encodeURIComponent(value);
                if (typeof daysToLive === 'number') {
                    cookie += '; max-age=' + (daysToLive * 24 * 60 * 60) + '; domain=' + window.location.hostname + '; path=' + window.location.pathname;
                    document.cookie = cookie;
                }
            },
            toggleConfirmDeleteAction() {
                this.confirmIsOpened = !this.confirmIsOpened;
            },
            deleteCache() {
                this.deleteActionConfirmed = true;
                const cacheKey = this.cacheKey;
                //Remove existing caches
                if ('caches' in window) {
                    caches.keys().then(cacheNameList => {
                        return Promise.all(cacheNameList.map(cacheName => {
                            if(cacheName.includes(cacheKey)) {
                                console.log('"%s" cache deleted', cacheName);
                                return caches.delete(cacheName);
                            }
                        }));
                    });
                }

                //Remove cookie cacheDate
                window.document.cookie = 'cacheDate-' + cacheKey + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=' + window.location.hostname + '; path=' + window.location.pathname;
                const deleteBtn = this.$refs.deleteBtn;
                if (deleteBtn) {
                    deleteBtn.style.backgroundColor = '#34a44b';
                    deleteBtn.style.color = 'white';
                    deleteBtn.children[0].classList.add('reveal-text');
                }

                setTimeout(() => {
                    this.confirmIsOpened = false;
                    this.deleteActionConfirmed = false;
                    this.lastCacheDate = null;
                }, 3000);
            },
            loadWidgetRequestData() {
                let paramsQuery = this.removeParam('offline', window.location.search);
                if (this.language && !paramsQuery.includes('language'))
                    paramsQuery += '&language=' + this.language;

                // Push current program widget URL
                this.apiRequestsList.push(this.programUrl);

                this.widgetList.forEach(widget => {
                    if (widget.link !== undefined) {
                        let widgetUrl = widget.link;
                        if (widgetUrl.indexOf('&offline') === -1)
                            widgetUrl += this.offlineParameters;

                        this.apiRequestsList.push(widgetUrl);

                        if (['drone-view'].includes(widget.id))
                            this.apiRequestsList.push(this.apiUrl + '/request-resource/drone-view' + paramsQuery);
                    }
                });
            },
            // Return the current URL without the 'key' parameter
            removeParam(key, sourceURL) {
                let result = sourceURL.split('?')[0],
                param,
                params_arr = [],
                queryString = (sourceURL.indexOf('?') !== -1) ? sourceURL.split('?')[1] : '';

                if (queryString !== '') {
                    params_arr = queryString.split('&');
                    for (let i = params_arr.length - 1; i >= 0; i -= 1) {
                        param = params_arr[i].split('=')[0];
                        if (param === key)
                            params_arr.splice(i, 1);
                    }
                    result = result + '?' + params_arr.join('&');
                }
                return result;
            },
            // Start cache resources actions by clicking the download button (available only with offline parameter)
            startCache() {
                if (navigator.onLine && !this.startCaching) {

                    // Close sidebar to center loading animation
                    EventBus.$emit('toggleSidebar');

                    this.isLoadingOffline = true;

                    // Create cache cookie (six-month shelf life)
                    this.setCookie('cacheDate-' + this.cacheKey , new Date().toLocaleDateString('fr'), 182);
                    this.lastCacheDate = new Date().toLocaleDateString('fr');

                    this.loadWidgetRequestData();

                    //Prefetch requests data URLs
                    console.groupCollapsed('%c Request %c ' + this.apiRequestsList.length +' %c Data requests', this.consoleStyle.request, this.consoleStyle.counter, this.consoleStyle.text);
                    this.apiRequestsList.forEach(apiRequest => {
                        console.log('* ' + apiRequest);
                    });
                    console.groupEnd();
                    this.addToCache('Megawidget', 'requests', this.apiRequestsList, 'cors');

                     // Add to cache offline dependencies (Habiteo core, viewer, watermark, ...)
                    if (offlineDependencies) {
                        let fileList = [];

                        Object.keys(offlineDependencies).forEach(method => {
                            let sourceList = offlineDependencies[method];
                            fileList = [];
                            if (sourceList) {
                                Object.keys(sourceList).forEach(origin => {
                                    sourceList[origin].forEach(source => {
                                        switch (origin) {
                                            case 'S3':
                                                fileList.push(process.env.S3_URL + source);
                                                break;
                                            default:
                                                fileList.push(this.apiUrl + source);
                                                break;
                                        }
                                    });
                                });
                                console.groupCollapsed('%c Dependencies %c ' + fileList.length + ' %c offline dependencies files (core, viewer, watermark)', this.consoleStyle.dependencies, this.consoleStyle.counter, this.consoleStyle.text);
                                fileList.forEach(file => {
                                    console.log('*', file);
                                });
                                console.groupEnd();
                                this.addToCache('Frontoffice', 'resources', fileList, method);
                            }
                        });
                    }

                    // Prefetch API urls list
                    this.prefetchUrl(this.apiRequestsList);
                }
            },
            // Add files on web browser cache
            addToCache(app, category, requests, requestMode) {
                this.startCaching = true;
                let currentRequest;
                let loadingRequestSummary =  document.body.querySelector('#loadingRequestSummary');
                if (!loadingRequestSummary) {
                    loadingRequestSummary =  document.createElement('div');
                    loadingRequestSummary.setAttribute('id', 'loadingRequestSummary');
                    document.querySelector('#app').append(loadingRequestSummary);
                }

                let cacheName = `${app}-${category}`;
                if (!loadingRequestSummary.querySelector('#requestSummary')) {
                    let requestCountEl = document.createElement('div');
                    requestCountEl.setAttribute('id', 'requestSummary');
                    loadingRequestSummary.appendChild(requestCountEl);
                    this.requestList.loadedRequest = 0;
                    this.requestList.totalRequest = 0;
                }

                if (this.requestList[app+category] != null) {
                    //Increment existing cache requests counter
                    this.requestList[app+category].total += requests.length;
                } else {
                    //Create new counter display for current cache
                    let cacheLogEl = document.createElement('div');
                    cacheLogEl.setAttribute('id', app + category);
                    loadingRequestSummary.appendChild(cacheLogEl);
                    this.requestList[app + category] = {
                        'loaded' : 0,
                        'total' : requests.length
                    };
                }

                //Count all requests started
                this.requestList.totalRequest += requests.length;

                if (this.timeoutReset)
                    clearTimeout(this.timeoutReset);

                //Update the display for current cache count (loaded requests / total requests)
                loadingRequestSummary.querySelector('#requestSummary').innerHTML = 'Requests ' + this.requestList.totalRequest;
                let fullCacheName = `${cacheName}-${this.cacheKey}`;
                if (requestMode === 'cors') {
                    caches.open(fullCacheName).then(cache => {
                        return cache.addAll(requests.map(request => {
                            //Increment loaded cache requests counter for current cache
                            this.requestList[app + category].loaded += 1;

                            //Increment global loaded cache requests
                            this.requestList.loadedRequest += 1;

                            //Update the display for current cache count (loaded requests / total requests)
                            loadingRequestSummary.querySelector(`#${app}${category}`).innerHTML = cacheName +' (<span class="' + (this.requestList[app + category].loaded == this.requestList[app + category].total ? 'finished' : 'loading') + '">' + this.requestList[app + category].loaded + '</span>/<span class="' + (this.requestList[app + category].loaded == this.requestList[app + category].total ? 'finished' : '') + '">' + this.requestList[app + category].total + '</span>)';

                            return new Request(request, {mode: 'cors'});
                        })).then(() => {
                            if (this.requestList.loadedRequest === this.requestList.totalRequest)
                                if (document.body.querySelector('.loader-message'))
                                    this.startTimeoutResetDownloadDetails();
                        }).catch(error => console.error('Error during prefetch CORS URL : ', error));
                    });
                } else {
                    caches.open(fullCacheName).then(cache => {
                        return requests.map(request => {
                            //Update the display for current cache count (loaded requests / total requests)
                            loadingRequestSummary.querySelector('#' + app + category).innerHTML = cacheName +' (<span class="' + (this.requestList[app + category].loaded == this.requestList[app + category].total ? 'finished' : 'loading') + '">' + this.requestList[app + category].loaded + '</span>/<span class="' + (this.requestList[app + category].loaded == this.requestList[app + category].total ? 'finished' : '') + '">' + this.requestList[app + category].total + '</span>)';

                            //Update the display for current cache count (loaded requests / total requests)
                            loadingRequestSummary.querySelector('#requestSummary').innerHTML = 'Requests ' + this.requestList.totalRequest;
                            currentRequest = new Request(request, {mode: 'no-cors'});
                            fetch(currentRequest).then(response => {
                                cache.put(currentRequest, response);
                                return response;
                            }).then(result => {
                                //Increment loaded cache requests counter for current cache
                                this.requestList[app + category].loaded += 1;

                                //Increment global loaded cache requests
                                this.requestList.loadedRequest += 1;

                                //Update the display for current cache count (loaded requests / total requests)
                                loadingRequestSummary.querySelector('#' + app + category).innerHTML = cacheName +' (<span class="' + (this.requestList[app + category].loaded == this.requestList[app + category].total ? 'finished' : 'loading') + '">' + this.requestList[app + category].loaded + '</span>/<span class="' + (this.requestList[app + category].loaded == this.requestList[app + category].total ? 'finished' : '') + '">' + this.requestList[app + category].total + '</span>)';

                                if (this.requestList.loadedRequest === this.requestList.totalRequest)
                                    if (document.body.querySelector('.loader-message'))
                                        this.startTimeoutResetDownloadDetails();
                            }).catch(error => console.error('Error during prefetch NO-CORS URL : ', error));
                        });
                    });
                }
            },
            // Prefetch URL from an array to store request results
            prefetchUrl(urlList) {
                urlList.forEach(url => {
                    fetch(url).then(response => {
                        if (response.ok) {
                            response.text().then(result => {
                                if (result) {
                                    let jsonData = JSON.parse(result);
                                    Object.keys(jsonData).forEach(key => {
                                        if (jsonData[key] != undefined) {
                                            switch (key) {
                                                case 'images':
                                                    jsonData[key].forEach(content => {
                                                        // Special case : carousel object
                                                        if (!Array.isArray(content)) {
                                                            if(content.preview) {
                                                                console.log('%c Image ' + '%c preview found : ' + content.preview, this.consoleStyle.image, this.consoleStyle.text);
                                                                console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                                this.addToCache('Frontoffice', 'resources', [content.preview], 'no-cors');
                                                            }

                                                            if(content.url) {
                                                                console.log('%c Image ' + '%c URL found : ' + content.url, this.consoleStyle.image, this.consoleStyle.text);
                                                                console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                                this.addToCache('Frontoffice', 'resources', [content.url], 'no-cors');
                                                            }
                                                        } else {
                                                            console.groupCollapsed('%c Image ' + '%c ' + content.length +' ' + '%c images', this.consoleStyle.image, this.consoleStyle.counter, this.consoleStyle.text);
                                                            content.forEach(img => {
                                                                console.log('* ' + img);
                                                                this.addToCache('Frontoffice', 'resources', [img], 'no-cors');
                                                            });
                                                            console.groupEnd();
                                                            console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                        }
                                                    });
                                                    break;

                                                case 'gui':
                                                    if (jsonData[key].itinerary != undefined) {
                                                        console.groupCollapsed('%c Image ' + '%c ' + jsonData[key].itinerary.length +' ' + '%c GUI icons', this.consoleStyle.image, this.consoleStyle.counter, this.consoleStyle.text);
                                                        jsonData[key].itinerary.forEach(itinerary => {
                                                            console.log('%c * ' + itinerary.icon + ' and ' + itinerary.inactiveIcon, this.consoleStyle.text);
                                                            this.addToCache('Frontoffice', 'resources', [itinerary.icon, itinerary.inactiveIcon], 'cors');
                                                        });
                                                        console.groupEnd();
                                                        console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                    }
                                                    break;

                                                // 2D plan PDF file
                                                case 'source':
                                                    let pdfFileUrl = jsonData[key];
                                                    if (!this.isUrlExist(pdfFileUrl, 'files')) {
                                                        if (!this.urlRequestList.files)
                                                            this.urlRequestList.files = [];
                                                        this.urlRequestList.files.push(pdfFileUrl);
                                                        console.groupCollapsed('%c Resource ' + '%c 2D plan file', this.consoleStyle.resource, this.consoleStyle.text);
                                                        console.log(pdfFileUrl);
                                                        console.groupEnd();
                                                        console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                        this.addToCache('Frontoffice', 'resources', [pdfFileUrl], 'cors');
                                                    }
                                                    break;

                                                case 'levels':
                                                    let digitalModelUrlList = [];
                                                    jsonData[key].forEach((levelData, levelIndex) => {
                                                        let plotLevelUrl = [],
                                                        plotPreviewUrl = [],
                                                        currentPlotUrl = null,
                                                        currentDigitalModelLink = null;

                                                        if (levelData.hotspots) {
                                                            levelData.hotspots.forEach((hotspotData, hotspotIndex) => {
                                                                currentPlotUrl = hotspotData.link ? hotspotData.link + this.offlineParameters : null;
                                                                if (currentPlotUrl && !this.isUrlExist(currentPlotUrl, 'link')) {
                                                                    plotLevelUrl.push(currentPlotUrl);
                                                                    if (!this.urlRequestList.link)
                                                                        this.urlRequestList.link = [];
                                                                    this.urlRequestList.link.push(currentPlotUrl);
                                                                }

                                                                if (hotspotData.preview && !this.isUrlExist(hotspotData.preview, 'image')) {
                                                                    plotPreviewUrl.push(hotspotData.preview);
                                                                    if (!this.urlRequestList.image)
                                                                        this.urlRequestList.image = [];
                                                                    this.urlRequestList.image.push(hotspotData.preview);
                                                                }

                                                                if (plotPreviewUrl.length > 0) {
                                                                    this.addToCache('Frontoffice', 'resources', plotPreviewUrl, 'no-cors');
                                                                    console.groupCollapsed('%c Request ' + '%c ' + plotPreviewUrl.length +' ' + '%c 3D plan preview', this.consoleStyle.image, this.consoleStyle.counter, this.consoleStyle.text);

                                                                    plotPreviewUrl.forEach(previewUrl => {
                                                                        console.log('* Floor ' + levelIndex + ' preview URL : ' + previewUrl);
                                                                    });
                                                                    console.groupEnd();
                                                                    console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                                    plotPreviewUrl = [];
                                                                }

                                                                if (plotLevelUrl.length > 0) {
                                                                    this.addToCache('Megawidget', 'requests', plotLevelUrl, 'cors');
                                                                    console.groupCollapsed('%c Request ' + '%c ' + plotLevelUrl.length +' ' + '%c plot requests', this.consoleStyle.request, this.consoleStyle.counter, this.consoleStyle.text);

                                                                    plotLevelUrl.forEach(plotUrl => {
                                                                        console.log('* Floor ' + levelIndex + ' plot URL : ' + plotUrl);
                                                                    });
                                                                    console.groupEnd();
                                                                    console.log('   %c Origin ', this.consoleStyle.origin , url);

                                                                    // Prefetch all plot requests extracted from cutaway-view request-data API
                                                                    this.prefetchUrl(plotLevelUrl);
                                                                    plotLevelUrl = [];
                                                                }
                                                            });
                                                        }

                                                        if (levelData.images) {
                                                            console.groupCollapsed('%c Image ' + '%c ' + levelData.images.length +' ' + '%c Images from floor ' + levelIndex, this.consoleStyle.image, this.consoleStyle.counter, this.consoleStyle.text);
                                                            levelData.images[0].forEach(imageUrl => {
                                                                console.log('* ' + imageUrl);
                                                                this.addToCache('Frontoffice', 'resources', [imageUrl], 'no-cors');
                                                            });
                                                            console.groupEnd();
                                                            console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                        }

                                                        if (levelData.link) {
                                                            currentDigitalModelLink = levelData.link + this.offlineParameters;
                                                            if (currentDigitalModelLink && !this.isUrlExist(currentDigitalModelLink, 'link')) {
                                                                digitalModelUrlList.push(currentDigitalModelLink);
                                                                if (!this.urlRequestList.link)
                                                                    this.urlRequestList.link = [];
                                                                this.urlRequestList.link.push(currentDigitalModelLink);
                                                            }
                                                        }
                                                    });
                                                    if (digitalModelUrlList.length) {
                                                        this.addToCache('Megawidget', 'requests', digitalModelUrlList, 'cors');
                                                        console.groupCollapsed('%c Request(s) from hotspot ' + '%c ' + digitalModelUrlList.length +' ' + '%c digital model requests', this.consoleStyle.request, this.consoleStyle.counter, this.consoleStyle.text);

                                                        digitalModelUrlList.forEach(digitalModelUrl => {
                                                            console.log('* ' + digitalModelUrl);
                                                        });
                                                        console.groupEnd();
                                                        console.log('   %c Origin ', this.consoleStyle.origin , url);

                                                        // Prefetch all digital model requests extracted from hotspot
                                                        this.prefetchUrl(digitalModelUrlList);
                                                        digitalModelUrlList = [];
                                                    }
                                                    break;

                                                case 'hotspots':
                                                    let hotspotLinkList = [];
                                                    let hotspotPreviewList = [];
                                                    let hotspotImageList = [];
                                                    jsonData[key].forEach(hotspot => {
                                                        if (!hotspot.link)
                                                            return;

                                                        // Skip virtual-tour (not supported at the moment)
                                                        if (!this.isUrlExist(hotspot.link, 'link') && !hotspot.link.includes('virtual-tour')) {
                                                            let currentHostpotLink = hotspot.link + this.offlineParameters;
                                                            hotspotLinkList.push(currentHostpotLink);
                                                            if (!this.urlRequestList.link)
                                                                this.urlRequestList.link = [];
                                                            this.urlRequestList.link.push(currentHostpotLink);
                                                        } else if (hotspot.link.includes('virtual-tour'))
                                                            return;

                                                        if (hotspot.preview && !this.isUrlExist(hotspot.preview, 'image')) {
                                                            hotspotPreviewList.push(hotspot.preview);
                                                            if (!this.urlRequestList.image)
                                                                this.urlRequestList.image = [];
                                                            this.urlRequestList.image.push(hotspot.icon);
                                                        }

                                                        if (hotspot.images) {
                                                            hotspot.images.forEach(image => {
                                                                if (image.url && !this.isUrlExist(image.url, 'image')) {
                                                                    hotspotImageList.push(image.url);
                                                                    if (!this.urlRequestList.image)
                                                                        this.urlRequestList.image = [];
                                                                    this.urlRequestList.image.push(image.url);
                                                                }
                                                            });
                                                        }
                                                    });

                                                    if (hotspotPreviewList.length > 0) {
                                                        // Store in cache all hotspot previews
                                                        console.groupCollapsed('%c Hotspot preview ' + '%c ' + hotspotPreviewList.length +' ' + '%c Hotspot previews', this.consoleStyle.image, this.consoleStyle.counter, this.consoleStyle.text);
                                                        hotspotPreviewList.forEach(hotspotPreview => {
                                                            console.log('* ' + hotspotPreview);
                                                        });
                                                        console.groupEnd();
                                                        console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                        this.addToCache('Frontoffice', 'resources', hotspotPreviewList, 'cors');
                                                    }

                                                    if (hotspotImageList.length > 0) {
                                                        // Store in cache all hotspot images
                                                        console.groupCollapsed('%c Hotspot images ' + '%c ' + hotspotImageList.length +' ' + '%c Hotspot images', this.consoleStyle.image, this.consoleStyle.counter, this.consoleStyle.text);
                                                        hotspotImageList.forEach(hotspotImage => {
                                                            console.log('* ' + hotspotImage);
                                                        });
                                                        console.groupEnd();
                                                        console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                        this.addToCache('Frontoffice', 'resources', hotspotImageList, 'cors');
                                                    }

                                                    if (hotspotLinkList.length > 0) {
                                                        // Store in cache all hotspot requests
                                                        console.groupCollapsed('%c Hotspot link ' + '%c ' + hotspotLinkList.length +' ' + '%c Hotspot links', this.consoleStyle.request, this.consoleStyle.counter, this.consoleStyle.text);
                                                        hotspotLinkList.forEach(hotspotUrl => {
                                                            console.log('* ' + hotspotUrl);
                                                        });
                                                        console.groupEnd();
                                                        console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                        this.addToCache('Megawidget', 'requests', hotspotLinkList, 'cors');

                                                        // Prefetch all hotspot link requests to download the destination widget
                                                        this.prefetchUrl(hotspotLinkList);
                                                    }
                                                    break;

                                                // Medias from request-resources API
                                                case 'files':
                                                    if (jsonData[key]['cors']) {
                                                        console.groupCollapsed('%c Resource ' + '%c ' + jsonData[key]['cors'].length +' ' + '%c Resources list from request-resource API', this.consoleStyle.resource, this.consoleStyle.counter, this.consoleStyle.text);
                                                        jsonData[key]['cors'].forEach(resource => {
                                                            if (resource) {
                                                                console.log('* ' + resource);
                                                                if (resource.match('cloudfront.net'))
                                                                    this.addToCache('Cloudfront', 'resources', [resource], 'cors');
                                                                else
                                                                    this.addToCache('Frontoffice', 'resources', [resource], 'cors');
                                                            }
                                                        });
                                                        console.groupEnd();
                                                        console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                    }
                                                    break;

                                                case 'items':
                                                    jsonData[key].forEach(item => {
                                                        if (item.id) {
                                                            switch (item.id) {
                                                                case '3d-plan':
                                                                    let currentThreeDPreviewList = [], currentThreeDRequestList = [], isCached = false;
                                                                    // If there alternate model 3D plans
                                                                    if (item.submenu && item.submenu.length > 0) {
                                                                        item.submenu.forEach(subitem => {
                                                                            let current3DPlanUrl = subitem.link ? subitem.link + this.offlineParameters : null;
                                                                            if (current3DPlanUrl && !this.isUrlExist(current3DPlanUrl, 'link')) {
                                                                                currentThreeDRequestList.push(current3DPlanUrl);
                                                                                if (!this.urlRequestList.link)
                                                                                    this.urlRequestList.link = [];
                                                                                this.urlRequestList.link.push(current3DPlanUrl);
                                                                            }

                                                                            if (subitem.preview && !this.isUrlExist(subitem.preview, 'image')) {
                                                                                currentThreeDPreviewList.push(subitem.preview);
                                                                                if (!this.urlRequestList.image)
                                                                                    this.urlRequestList.image = [];
                                                                                this.urlRequestList.image.push(subitem.preview);
                                                                            }
                                                                        });

                                                                    if (currentThreeDPreviewList && currentThreeDPreviewList.length > 0) {
                                                                        console.log('%c Image ' + '%c 3D plan previews : ' + currentThreeDPreviewList, this.consoleStyle.image, this.consoleStyle.text);
                                                                        console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                                        this.addToCache('Frontoffice', 'resources', currentThreeDPreviewList, 'no-cors');
                                                                    }

                                                                    if (currentThreeDRequestList.length > 0) {
                                                                        this.addToCache('Megawidget', 'requests', currentThreeDRequestList, 'cors');
                                                                        console.groupCollapsed('%c 3D API Request ' + '%c ' + currentThreeDRequestList.length +' ' + '%c 3D plan requests', this.consoleStyle.request, this.consoleStyle.counter, this.consoleStyle.text);

                                                                        currentThreeDRequestList.forEach(threeDRequestUrl => {
                                                                            console.log('* ' + threeDRequestUrl);
                                                                        });
                                                                        console.groupEnd();
                                                                        console.log('   %c Origin ', this.consoleStyle.origin , url);

                                                                        // Prefetch all 3D plan widget requests extracted from plot request-data API
                                                                        this.prefetchUrl(currentThreeDRequestList);
                                                                        currentThreeDRequestList = [];
                                                                    }
                                                                } else {
                                                                    if (item.link) {
                                                                        let threeDUrl = item.link + this.offlineParameters;
                                                                        if (!this.isUrlExist(threeDUrl, 'link')) {
                                                                            if (!this.urlRequestList.link)
                                                                                this.urlRequestList.link = [];
                                                                            this.urlRequestList.link.push(threeDUrl);
                                                                            console.log('%c Request ' + '%c 3D plan request : ' + threeDUrl, this.consoleStyle.request, this.consoleStyle.text);
                                                                            console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                                            this.addToCache('Megawidget', 'requests', [threeDUrl], 'cors');

                                                                            this.prefetchUrl([threeDUrl]);
                                                                        }
                                                                    }

                                                                    if (item.preview) {
                                                                        if (!this.isUrlExist(item.preview, 'image')) {
                                                                            if (!this.urlRequestList.image)
                                                                                this.urlRequestList.image = [];
                                                                            this.urlRequestList.image.push(item.preview);
                                                                            console.log('%c Image ' + '%c 3D plan preview : ' + item.preview, this.consoleStyle.image, this.consoleStyle.text);
                                                                            console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                                            this.addToCache('Frontoffice', 'resources', [item.preview], 'no-cors');
                                                                        }
                                                                    }
                                                                }
                                                                break;

                                                                case '2d-plan':
                                                                    if (item.link) {
                                                                        let plotUrl = item.link + this.offlineParameters;
                                                                        if (!this.isUrlExist(plotUrl, 'link')) {
                                                                            if (!this.urlRequestList.link)
                                                                                this.urlRequestList.link = [];
                                                                            this.urlRequestList.link.push(plotUrl);
                                                                            console.log('%c Request ' + '%c 2D plan request : ' + plotUrl, this.consoleStyle.request, this.consoleStyle.text);
                                                                            console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                                            this.addToCache('Megawidget', 'requests', [plotUrl], 'cors');
                                                                            this.prefetchUrl([plotUrl]);
                                                                        }
                                                                    }
                                                                    break;

                                                                default:
                                                                    if (item.preview) {
                                                                        console.log('%c Image ' + '%c ' + item.id + ' preview : ' + item.preview, this.consoleStyle.image, this.consoleStyle.text);
                                                                        console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                                        this.addToCache('Frontoffice', 'resources', [item.preview], 'no-cors');
                                                                    }

                                                                    if (item.icon) {
                                                                        console.log('%c Image ' + '%c ' + item.id + ' icon : ' + item.icon, this.consoleStyle.image, this.consoleStyle.text);
                                                                        console.log('   %c Origin ', this.consoleStyle.origin , url);
                                                                        this.addToCache('Frontoffice', 'resources', [item.icon], 'no-cors');
                                                                    }
                                                                    break;
                                                            }
                                                        }
                                                    });
                                                    break;

                                                default:
                                                    break;
                                            }
                                        }
                                    });
                                }
                            });
                        } else {
                            console.error('Network request for "' + url + '" failed with response ' + response.status + ': ' + response.statusText);
                        }
                    });
                });
            },
            // Start or reset timeout to remove download animation
            startTimeoutResetDownloadDetails() {
                if (this.timeoutReset)
                    clearTimeout(this.timeoutReset);
                this.timeoutReset = setTimeout(() => {
                    this.resetDownloadDetails()
                }, 10000);
            },
            // Refresh page once the timeout is down
            resetDownloadDetails() {
                document.body.querySelector('#loadingRequestSummary').innerHTML = '<div id="requestSummary">Refresh in progress ...</div>';
                setTimeout(() => {
                    window.location.reload(1);
                }, 2000);
            },
            isUrlExist(url, type) {
                if (this.urlRequestList[type])
                    return this.urlRequestList[type].includes(url);
                return false;
            }
        }
    };
</script>

<style lang="scss">
    @import "@/assets/scss/components/_offline.scss";
    @import "@/assets/scss/components/_information-card.scss";
</style>



// WEBPACK FOOTER //
// src/components/NetworkStatus.vue